// The matrix is not drawn here — it is the Drift Management screen's own chart, lifted into
// components/charts so both surfaces render the same picture. This file only maps a normalized
// chat chart onto its props, exactly as ChatDoughnut and ChatScatter do for theirs.
import type { DriftHistoryPoint } from "../../../../api/frameworksApi";
import { ScanTimelineMatrix } from "../../charts/ScanTimelineMatrix";
import { TIMELINE_ROWS_MAX_PX, type NormalizedChart } from "./resolveChart";

/** Hours between labelled ticks. The component widens this further if the column is narrower. */
const CHAT_HOUR_TICK_INTERVAL = 6;
/** Narrower than the screen's 88px — a chat column cannot spare a quarter of its width to labels. */
const CHAT_LABEL_WIDTH = 52;
/** Half-width of a scan diamond. The hover target under it stays reachable regardless. */
const CHAT_MARK_RADIUS = 7;
/**
 * The rows scroll past {@link TIMELINE_ROWS_MAX_PX}.
 *
 * A control scanned four times a day for three months has twenty-odd day rows, which uncapped came
 * to roughly 990px — three times the chart it replaced, and enough to push the answer it belongs to
 * off screen. Capping it keeps the card the size of every other chart in the conversation; the
 * older dates are a scroll away rather than gone.
 *
 * <p>Imported rather than redeclared: `chartHeight` reserves the card's box before this mounts, and
 * two copies of the number would eventually disagree by exactly the gap the reservation exists to
 * prevent.
 */

/**
 * Rows to scan history.
 *
 * The block carries one row per scan with the raw `timestamp` and lowercase `status` — the shape
 * the matrix already consumes, so nothing is transformed, only renamed into place. `opaDetails` is
 * deliberately absent: it drives the screen's evidence drill-down, and the chat panel has nowhere
 * to open one.
 */
function toHistory(chart: NormalizedChart): DriftHistoryPoint[] {
  const out: DriftHistoryPoint[] = [];
  for (const row of chart.rows) {
    const timestamp = row[chart.categoryKey];
    if (typeof timestamp !== "string" || !timestamp) continue;
    const raw = String(row.status ?? "").toLowerCase();
    out.push({
      timestamp,
      status: raw === "passed" || raw === "failed" ? raw : "unknown",
    });
  }
  return out;
}

export function ChatScanTimeline({ chart }: { chart: NormalizedChart }) {
  return (
    <ScanTimelineMatrix
      history={toHistory(chart)}
      hourTickInterval={CHAT_HOUR_TICK_INTERVAL}
      labelWidth={CHAT_LABEL_WIDTH}
      rowHeight={chart.timelineRowHeight}
      // The screen's 8px suits its 64px rows. These are 44px in a much narrower column, where the
      // same mark reads as chunky and scans minutes apart run into each other.
      markRadius={CHAT_MARK_RADIUS}
      // Dimming the rest already says which scans matched. At this size, growing the matches too
      // reads as the marks changing size rather than as a filter being applied.
      emphasiseMatches={false}
      maxHeight={TIMELINE_ROWS_MAX_PX}
      // The drift screen filters by status from its KPI cards. There are none here, so without a
      // clickable legend there is no way to isolate the failures at all.
      interactiveLegend
      // No onPointClick: the screen opens an evidence drawer from a scan, and there is nowhere in
      // a chat card to put one. Omitting it also drops the "Read more" the tooltip would offer.
    />
  );
}
